// Seafloor generation (GPU compute) and rendering.
//
// The world is a basin: a sandy floor with rock outcrops, ringed by cliffs that
// rise toward the surface, with one gap that drops away into deep water. The
// camera is kept inside the basin so the edges of the generated world are
// never visible.

import {createShader} from '../gpu/device.ts';
import {boxOutside, sidePlanes} from '../render/frustum.ts';
import type {CullView} from '../render/renderer.ts';
import {defineStruct, StructBuffer} from '../gpu/structs.ts';
import {dispatch2D, readBuffer} from '../gpu/util.ts';
import {noise, surfaceLib} from '../shaders/index.ts';
import type {Rng} from '../core/rng.ts';
import propsWgsl from '../shaders/props.wgsl';

export const TerrainParams = defineStruct('TerrainParams', {
  seed: 'u32',
  size: 'u32',
  worldSize: 'f32',
  floorDepth: 'f32',
  center: 'vec2f',
  basinRadius: 'f32',
  rimHeight: 'f32',
  gapAngle: 'f32',
  gapWidth: 'f32',
  duneAmp: 'f32',
  rockiness: 'f32',
  warp: 'f32',
  surfaceY: 'f32',
  outcropScale: 'f32',
  spurHeight: 'f32',
  canyonDepth: 'f32',
  canyonWidth: 'f32',
  canyonScale: 'f32',
});

export interface TerrainSettings {
  seed: number;
  size: number;
  worldSize: number;
  floorDepth: number;
  center: [number, number];
  basinRadius: number;
  rimHeight: number;
  gapAngle: number;
  gapWidth: number;
  duneAmp: number;
  rockiness: number;
  warp: number;
  surfaceY: number;
  outcropScale: number;
  /** Height of the spur-and-groove reef ridges leading to the drop-off. */
  spurHeight: number;
  /** Depth of the main canyon floors below the surrounding seabed. */
  canyonDepth: number;
  /** Half-width of a main canyon at the rim, in metres. */
  canyonWidth: number;
  /** Size of the channel network: smaller means longer, straighter canyons. */
  canyonScale: number;
}

export function randomTerrainSettings(
  rng: Rng,
  surfaceY: number,
): TerrainSettings {
  return {
    seed: rng.nextU32(),
    size: 512,
    worldSize: 320,
    floorDepth: rng.range(-19, -14),
    center: [0, 0],
    basinRadius: rng.range(52, 64),
    rimHeight: rng.range(7, 10),
    gapAngle: rng.range(0, Math.PI * 2),
    gapWidth: rng.range(0.45, 0.8),
    duneAmp: rng.range(0.8, 2.0),
    rockiness: rng.range(0.6, 1.3),
    warp: rng.range(0.6, 1.2),
    surfaceY,
    outcropScale: rng.range(0.8, 1.25),
    spurHeight: rng.range(2.5, 4.5),
    canyonDepth: rng.range(9, 14),
    canyonWidth: rng.range(4.5, 6.5),
    canyonScale: rng.range(0.8, 1.2),
  };
}

const genShader = /* wgsl */ `${noise} ${TerrainParams.wgsl} @group(0)@binding(0)var<uniform>P:TerrainParams;@group(0)@binding(1)var<storage,read_write>heights:array<f32>;fn spurMask(p:vec2f)->f32{let gapDir=vec2f(cos(P.gapAngle),sin(P.gapAngle));let side=vec2f(-gapDir.y,gapDir.x);let d=p-P.center;let along=dot(d,gapDir);let across=dot(d,side);let R=P.basinRadius;let zone=smoothstep(-R*0.25,R*0.25,along)*smoothstep(R*1.0,R*0.75,along)*smoothstep(R*0.85,R*0.35,abs(across));let groove=across*0.62+fbm2(p*0.04+5.0,3)*2.6;let wave=0.5+0.5*sin(groove);let rounded=smoothstep(0.15,0.95,wave);let knobs=0.65+0.35*smoothstep(-0.35,0.35,fbm2(vec2f(along*0.16,across*0.05)+13.0,3));let lumps=1.0+fbm2(p*0.32+21.0,2)*0.35;return zone*rounded*knobs*lumps;}fn canyonCut(p:vec2f)->f32{let flow=vec2f(cos(P.gapAngle),sin(P.gapAngle));let across=vec2f(-flow.y,flow.x);let bend=fbm2(p*0.004+61.0,2)*0.5;let g=vec2f(dot(p,flow)*(0.3+bend*0.5),dot(p,across))*0.0085*P.canyonScale;let warpV=vec2f(fbm2(g*1.7+5.0,4),fbm2(g*1.7+19.0,4));let q=g+warpV*0.7;let region=smoothstep(0.1,0.55,fbm2(p*0.0075+47.0,3)+0.28);if(region<=0.001){return 0.0;}let rimNoise=1.0+fbm2(q*9.0+27.0,3)*0.35;let trunk=abs(fbm2(q,4));let trunkW=P.canyonWidth*0.019*P.canyonScale*rimNoise;var cut=(1.0-smoothstep(trunkW*0.78,trunkW,trunk))*P.canyonDepth;let branch=abs(fbm2(q*2.2+3.3,4));let branchW=trunkW*0.5;cut+=(1.0-smoothstep(branchW*0.7,branchW,branch))*P.canyonDepth*0.45;let rill=abs(fbm2(q*6.0+11.0,3));let rillW=trunkW*0.26;cut+=(1.0-smoothstep(rillW*0.55,rillW,rill))*P.canyonDepth*0.14;cut=min(cut,P.canyonDepth*1.1)*region;let along=dot(p-P.center,flow)/max(P.basinRadius,1.0);cut*=0.65+0.5*smoothstep(-1.0,1.0,along);let r=length(p-P.center);cut*=smoothstep(P.basinRadius+6.0,P.basinRadius-16.0,r);return cut;}fn terrace(h:f32,step:f32,strength:f32)->f32{let t=h/step;let f=fract(t);let shaped=floor(t)+smoothstep(0.25,0.75,f);return mix(h,shaped*step,strength);}fn basinHeight(p:vec2f)->f32{let warpV=vec2f(fbm2(p*0.008,3),fbm2(p*0.008+vec2f(41.0,17.0),3));let q=p+warpV*30.0*P.warp;let d=q-P.center;let r=length(d);var h=P.floorDepth;h+=fbm2(p*0.018,4)*2.2;let duneDir=normalize(vec2f(0.8,0.6));let dunePhase=dot(q,duneDir)*0.11+fbm2(p*0.03,2)*1.5;h+=(abs(sin(dunePhase))-0.6)*P.duneAmp*(0.5+0.5*fbm2(p*0.02+7.0,2));let rid=ridged2(q*0.035*P.outcropScale,4);let outcropMask=smoothstep(0.5,0.9,rid)*smoothstep(0.1,0.35,fbm2(q*0.015+3.0,3)+0.25);let mound=outcropMask*outcropMask*(3.0-2.0*outcropMask);h+=mound*3.2*P.rockiness*(0.7+0.5*fbm2(q*0.06,2));h+=spurMask(p)*P.spurHeight*(0.75+0.35*fbm2(p*0.07+9.0,3));let cut=canyonCut(p);if(cut>0.05){let floorNoise=fbm2(p*0.09+31.0,3)*0.8;h=terrace(h-cut+floorNoise*smoothstep(1.0,6.0,cut),2.2,0.5*smoothstep(1.0,5.0,cut));}let angle=atan2(d.y,d.x);var ad=abs(angle-P.gapAngle);ad=min(ad,6.2831853-ad);let gap=smoothstep(P.gapWidth,P.gapWidth*0.35,ad);let rimT=smoothstep(P.basinRadius-4.0,P.basinRadius+34.0,r);let cliffNoise=ridged2(q*0.028,6);var rim=rimT*(P.rimHeight+cliffNoise*9.0);rim+=rimT*(fract(rim*0.33)-0.5)*0.9;h+=rim*(1.0-gap);let drop=smoothstep(P.basinRadius-8.0,P.basinRadius+70.0,r);h-=gap*drop*45.0;let cap=P.surfaceY-4.0-fbm2(p*0.05,2)*2.0;let k=4.0;let hh=clamp(0.5+0.5*(cap-h)/k,0.0,1.0);h=mix(cap,h,hh)-k*hh*(1.0-hh);return h;}@compute@workgroup_size(8,8)fn main(@builtin(global_invocation_id)id:vec3u){if(any(id.xy>=vec2u(P.size))){return;}noiseSeed=P.seed;let uv=(vec2f(id.xy)+0.5)/f32(P.size);let p=(uv-0.5)*P.worldSize;heights[id.y*P.size+id.x]=basinHeight(p);}`;

const deriveShader = /* wgsl */ `${noise} ${TerrainParams.wgsl} @group(0)@binding(0)var<uniform>P:TerrainParams;@group(0)@binding(1)var<storage,read>heights:array<f32>;@group(0)@binding(2)var<storage,read_write>data:array<vec4f>;@group(0)@binding(3)var<storage,read_write>masks:array<vec4f>;@group(0)@binding(4)var dataTex:texture_storage_2d<rgba16float,write>;@group(0)@binding(5)var maskTex:texture_storage_2d<rgba8unorm,write>;fn spurMask(p:vec2f)->f32{let gapDir=vec2f(cos(P.gapAngle),sin(P.gapAngle));let side=vec2f(-gapDir.y,gapDir.x);let d=p-P.center;let along=dot(d,gapDir);let across=dot(d,side);let R=P.basinRadius;let zone=smoothstep(-R*0.25,R*0.25,along)*smoothstep(R*1.0,R*0.75,along)*smoothstep(R*0.85,R*0.35,abs(across));let groove=across*0.62+fbm2(p*0.04+5.0,3)*2.6;let wave=0.5+0.5*sin(groove);let rounded=smoothstep(0.15,0.95,wave);let knobs=0.65+0.35*smoothstep(-0.35,0.35,fbm2(vec2f(along*0.16,across*0.05)+13.0,3));let lumps=1.0+fbm2(p*0.32+21.0,2)*0.35;return zone*rounded*knobs*lumps;}fn H(x:i32,y:i32)->f32{let s=i32(P.size);let cx=clamp(x,0,s-1);let cy=clamp(y,0,s-1);return heights[u32(cy*s+cx)];}@compute@workgroup_size(8,8)fn main(@builtin(global_invocation_id)id:vec3u){if(any(id.xy>=vec2u(P.size))){return;}noiseSeed=P.seed^0x5bd1e995u;let x=i32(id.x);let y=i32(id.y);let texel=P.worldSize/f32(P.size);let h=H(x,y);let dx=(H(x+1,y)-H(x-1,y))/(2.0*texel);let dz=(H(x,y+1)-H(x,y-1))/(2.0*texel);let n=normalize(vec3f(-dx,1.0,-dz));var occl=0.0;for(var k=0;k<8;k++){let a=f32(k)*0.785398;let dir=vec2f(cos(a),sin(a));var maxSlope= -1.0;var dist=1.0;for(var s=0;s<7;s++){let o=vec2i(round(dir*dist));let dh=H(x+o.x,y+o.y)-h;maxSlope=max(maxSlope,dh/(dist*texel));dist*=1.8;}occl+=clamp(atan(maxSlope)/1.5708,0.0,1.0);}let ao=clamp(1.0-occl/8.0*1.25,0.0,1.0);let uv=(vec2f(id.xy)+0.5)/f32(P.size);let p=(uv-0.5)*P.worldSize;let lap=(H(x+2,y)+H(x-2,y)+H(x,y+2)+H(x,y-2)-4.0*h)/(4.0*texel*texel);let slope=1.0-n.y;var rock=smoothstep(0.12,0.35,slope)+smoothstep(0.0,-0.25,lap)*0.6;rock=clamp(rock+(fbm2(p*0.07,3))*0.3,0.0,1.0);var reef=clamp(smoothstep(-0.1,0.35,fbm2(p*0.025+19.0,4))*(1.0-smoothstep(0.55,0.9,slope)),0.0,1.0);let spur=spurMask(p);reef=max(reef,smoothstep(0.25,0.7,spur));rock=max(rock,smoothstep(0.2,0.6,spur)*0.95);let kelp=clamp(smoothstep(0.0,0.3,fbm2(p*0.02+71.0,3))*(1.0-rock),0.0,1.0);let moss=clamp(rock*smoothstep(0.55,0.95,n.y)+fbm2(p*0.2,2)*0.2,0.0,1.0);let i=id.y*P.size+id.x;data[i]=vec4f(h,n.x,n.z,ao);masks[i]=vec4f(rock,reef,kelp,moss);textureStore(dataTex,id.xy,vec4f(h,n.x,n.z,ao));textureStore(maskTex,id.xy,vec4f(rock,reef,kelp,moss));}`;

export class TerrainData {
  readonly size: number;
  readonly worldSize: number;
  readonly data: Float32Array;
  readonly masks: Float32Array;
  /** Vertices per side of the drawn mesh; set once the tier is known. */
  meshGrid = 0;
  /** Ground heights from elsewhere (the volumetric terrain), if in use. */
  private ground: {grid: number; data: Float32Array} | null = null;

  /** Use these heights as the ground rather than the height map. */
  setGroundOverride(grid: number, data: Float32Array) {
    this.ground = {grid, data};
  }
  constructor(
    size: number,
    worldSize: number,
    data: Float32Array,
    masks: Float32Array,
  ) {
    this.size = size;
    this.worldSize = worldSize;
    this.data = data;
    this.masks = masks;
  }

  private sample(
    arr: Float32Array,
    channel: number,
    x: number,
    z: number,
  ): number {
    const s = this.size;
    const fx = Math.min(
      Math.max((x / this.worldSize + 0.5) * s - 0.5, 0),
      s - 1.001,
    );
    const fz = Math.min(
      Math.max((z / this.worldSize + 0.5) * s - 0.5, 0),
      s - 1.001,
    );
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    const at = (xx: number, zz: number) => arr[(zz * s + xx) * 4 + channel];
    const a = at(ix, iz) + (at(ix + 1, iz) - at(ix, iz)) * tx;
    const b = at(ix, iz + 1) + (at(ix + 1, iz + 1) - at(ix, iz + 1)) * tx;
    return a + (b - a) * tz;
  }

  heightAt(x: number, z: number) {
    return this.sample(this.data, 0, x, z);
  }

  /**
   * The height of the seabed *as drawn*: the mesh interpolates between its own
   * vertices, and its grid is coarser than the height map, so a crest that
   * falls between two vertices is cut off. Anything that must agree with what
   * you can see — where a plant is rooted, where the camera is stopped — has to
   * use this rather than the height map, or it ends up rooted in mid-water, or
   * blocked by a ridge that was never drawn.
   */
  groundAt(x: number, z: number): number {
    const o = this.ground;
    if (o) {
      const g = o.grid;
      const fx = Math.min(
        Math.max((x / this.worldSize + 0.5) * g - 0.5, 0),
        g - 1.001,
      );
      const fz = Math.min(
        Math.max((z / this.worldSize + 0.5) * g - 0.5, 0),
        g - 1.001,
      );
      const ix = Math.floor(fx);
      const iz = Math.floor(fz);
      const tx = fx - ix;
      const tz = fz - iz;
      const at = (a: number, b: number) => o.data[b * g + a];
      return (
        (at(ix, iz) + (at(ix + 1, iz) - at(ix, iz)) * tx) * (1 - tz) +
        (at(ix, iz + 1) + (at(ix + 1, iz + 1) - at(ix, iz + 1)) * tx) * tz
      );
    }
    const grid = this.meshGrid;
    if (!grid) {
      return this.heightAt(x, z);
    }
    const half = this.worldSize / 2;
    // The mesh grid is warped (denser in the middle): undo that to find which
    // two vertices a point falls between.
    const cell = (w: number) => {
      const g =
        Math.sign(w) * Math.pow(Math.min(Math.abs(w) / half, 1), 1 / 1.6);
      return ((g + 1) / 2) * grid;
    };
    const world = (i: number) => {
      const g = (i / grid) * 2 - 1;
      return Math.sign(g) * Math.pow(Math.abs(g), 1.6) * half;
    };
    const gx = cell(x);
    const gz = cell(z);
    const ix = Math.max(0, Math.min(grid - 1, Math.floor(gx)));
    const iz = Math.max(0, Math.min(grid - 1, Math.floor(gz)));
    const tx = Math.max(0, Math.min(1, gx - ix));
    const tz = Math.max(0, Math.min(1, gz - iz));
    const x0 = world(ix);
    const x1 = world(ix + 1);
    const z0 = world(iz);
    const z1 = world(iz + 1);
    const h00 = this.heightAt(x0, z0);
    const h10 = this.heightAt(x1, z0);
    const h01 = this.heightAt(x0, z1);
    const h11 = this.heightAt(x1, z1);
    return (h00 + (h10 - h00) * tx) * (1 - tz) + (h01 + (h11 - h01) * tx) * tz;
  }
  normalAt(x: number, z: number): [number, number, number] {
    const nx = this.sample(this.data, 1, x, z);
    const nz = this.sample(this.data, 2, x, z);
    return [nx, Math.sqrt(Math.max(0, 1 - nx * nx - nz * nz)), nz];
  }
  aoAt(x: number, z: number) {
    return this.sample(this.data, 3, x, z);
  }
  /** 0 rock, 1 reef, 2 kelp, 3 moss */
  maskAt(channel: number, x: number, z: number) {
    return this.sample(this.masks, channel, x, z);
  }
}

export interface TerrainGpu {
  texture: GPUTexture;
  maskTexture: GPUTexture;
  cpu: TerrainData;
}

export async function generateTerrain(
  device: GPUDevice,
  s: TerrainSettings,
): Promise<TerrainGpu> {
  const N = s.size;
  const params = new StructBuffer(TerrainParams);
  for (const [k, v] of Object.entries(s)) {
    params.set(k as keyof typeof TerrainParams.fields, v as number | number[]);
  }
  const paramBuf = device.createBuffer({
    label: 'terrain:params',
    size: TerrainParams.size,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(paramBuf, 0, params.data);

  const heights = device.createBuffer({
    label: 'terrain:heights',
    size: N * N * 4,
    usage: GPUBufferUsage.STORAGE,
  });
  const dataBuf = device.createBuffer({
    label: 'terrain:data',
    size: N * N * 16,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const maskBuf = device.createBuffer({
    label: 'terrain:masks',
    size: N * N * 16,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const texture = device.createTexture({
    label: 'terrain:texture',
    size: [N, N],
    format: 'rgba16float',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING,
  });
  const maskTexture = device.createTexture({
    label: 'terrain:mask-texture',
    size: [N, N],
    format: 'rgba8unorm',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING,
  });

  const genModule = createShader(device, 'terrain:gen-shader', genShader);
  const genPipeline = await device.createComputePipelineAsync({
    label: 'terrain:gen-pipeline',
    layout: 'auto',
    compute: {module: genModule, entryPoint: 'main'},
  });
  const deriveModule = createShader(
    device,
    'terrain:derive-shader',
    deriveShader,
  );
  const derivePipeline = await device.createComputePipelineAsync({
    label: 'terrain:derive-pipeline',
    layout: 'auto',
    compute: {module: deriveModule, entryPoint: 'main'},
  });

  dispatch2D(
    device,
    'terrain:gen',
    genPipeline,
    [
      device.createBindGroup({
        label: 'terrain:gen-bind-group',
        layout: genPipeline.getBindGroupLayout(0),
        entries: [
          {binding: 0, resource: {buffer: paramBuf}},
          {binding: 1, resource: {buffer: heights}},
        ],
      }),
    ],
    N,
    N,
  );
  dispatch2D(
    device,
    'terrain:derive',
    derivePipeline,
    [
      device.createBindGroup({
        label: 'terrain:derive-bind-group',
        layout: derivePipeline.getBindGroupLayout(0),
        entries: [
          {binding: 0, resource: {buffer: paramBuf}},
          {binding: 1, resource: {buffer: heights}},
          {binding: 2, resource: {buffer: dataBuf}},
          {binding: 3, resource: {buffer: maskBuf}},
          {
            binding: 4,
            resource: texture.createView({label: 'terrain:data-storage-view'}),
          },
          {
            binding: 5,
            resource: maskTexture.createView({
              label: 'terrain:mask-storage-view',
            }),
          },
        ],
      }),
    ],
    N,
    N,
  );

  const [data, masks] = await Promise.all([
    readBuffer(device, dataBuf),
    readBuffer(device, maskBuf),
  ]);
  heights.destroy();
  dataBuf.destroy();
  maskBuf.destroy();
  return {
    texture,
    maskTexture,
    cpu: new TerrainData(
      N,
      s.worldSize,
      new Float32Array(data),
      new Float32Array(masks),
    ),
  };
}

/**
 * Reads the height texture back through the GPU and compares it with the CPU
 * copy the world was built from.
 *
 * Everything is placed from the CPU copy and drawn from the texture, so if a
 * device wrote one and not the other, props would stand in mid-water over a
 * seabed that is somewhere else entirely — and nothing would say so.
 */
export async function verifyHeightTexture(
  device: GPUDevice,
  texture: GPUTexture,
  cpu: TerrainData,
): Promise<string> {
  const n = 32;
  const shader = createShader(
    device,
    'terrain:verify-shader',
    /* wgsl */ `@group(0)@binding(0)var tex:texture_2d<f32>;@group(0)@binding(1)var<storage,read_write>out:array<f32>;@compute@workgroup_size(8,8)fn main(@builtin(global_invocation_id)id:vec3u){if(any(id.xy>=vec2u(${n}u))){return;}let size=textureDimensions(tex,0);let c=vec2u((id.xy*size)/ ${n}u);out[id.y* ${n}u+id.x]=textureLoad(tex,c,0).r;}`,
  );
  const pipeline = await device.createComputePipelineAsync({
    label: 'terrain:verify-pipeline',
    layout: 'auto',
    compute: {module: shader, entryPoint: 'main'},
  });
  const buf = device.createBuffer({
    label: 'terrain:verify',
    size: n * n * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const enc = device.createCommandEncoder({label: 'terrain:verify-encoder'});
  const pass = enc.beginComputePass({label: 'terrain:verify-pass'});
  pass.setPipeline(pipeline);
  pass.setBindGroup(
    0,
    device.createBindGroup({
      label: 'terrain:verify-bind-group',
      layout: pipeline.getBindGroupLayout(0),
      entries: [
        {
          binding: 0,
          resource: texture.createView({label: 'terrain:verify-view'}),
        },
        {binding: 1, resource: {buffer: buf}},
      ],
    }),
  );
  pass.dispatchWorkgroups(Math.ceil(n / 8), Math.ceil(n / 8));
  pass.end();
  device.queue.submit([enc.finish({label: 'terrain:verify'})]);
  const got = new Float32Array(await readBuffer(device, buf));
  buf.destroy();
  let worst = 0;
  let zeros = 0;
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const texel = Math.floor((i * cpu.size) / n);
      const texelZ = Math.floor((j * cpu.size) / n);
      const want = cpu.data[(texelZ * cpu.size + texel) * 4];
      const have = got[j * n + i];
      worst = Math.max(worst, Math.abs(have - want));
      if (have === 0) {
        zeros++;
      }
    }
  }
  return `height texture: worst ${worst.toFixed(2)} m, ${zeros}/${n * n} zero`;
}

// ---------------------------------------------------------------------------
// Rendering

export const terrainMaterialWgsl = /* wgsl */ `fn triplanar(p:vec3f,n:vec3f,scale:f32)->vec4f{var w=pow(abs(n),vec3f(4.0));w/=(w.x+w.y+w.z);let x=textureSample(tDetail,sLinearRepeat,p.zy*scale);let y=textureSample(tDetail,sLinearRepeat,p.xz*scale);let z=textureSample(tDetail,sLinearRepeat,p.xy*scale);return x*w.x+y*w.y+z*w.z;}fn triplanarLod(p:vec3f,n:vec3f,scale:f32,footprint:f32)->vec4f{var w=pow(abs(n),vec3f(8.0));w/=(w.x+w.y+w.z);let lod=log2(max(footprint*scale*512.0,1.0))+1.0;let x=textureSampleLevel(tDetail,sLinearRepeat,p.zy*scale,lod);let y=textureSampleLevel(tDetail,sLinearRepeat,p.xz*scale,lod);let z=textureSampleLevel(tDetail,sLinearRepeat,p.xy*scale,lod);return x*w.x+y*w.y+z*w.z;}fn rockRelief(q:vec3f,n:vec3f,reef:f32,e:f32)->f32{return triplanarLod(q,n,0.13,e).r*1.2+triplanarLod(q,n,0.5,e).r*0.3+triplanarLod(q,n,0.42,e).r*0.8*reef;}fn rockBump(n:vec3f,p:vec3f,reef:f32,strength:f32,footprint:f32)->vec3f{let a=select(vec3f(1.0,0.0,0.0),vec3f(0.0,0.0,1.0),abs(n.x)>0.7);let t1=normalize(cross(n,a));let t2=cross(n,t1);let e=footprint;let h0=rockRelief(p,n,reef,e);let h1=rockRelief(p+t1*e,n,reef,e);let h2=rockRelief(p+t2*e,n,reef,e);let g=(t1*(h1-h0)+t2*(h2-h0))/e;return normalize(n-g*strength);}struct FOut{@location(0)color:vec4f,@location(1)velocity:vec2f,@location(2)id:u32,};fn terrainSurface(p:vec3f,nIn:vec3f,m:vec4f,aoIn:f32)->Surface{var n=nIn;let rockAmt=m.r;let mossAmt=m.a;let broad=textureSample(tDetail,sLinearRepeat,p.xz*0.02);let tri=triplanar(p,n,0.13);let triFine=triplanar(p,n,0.55);let sandDetail=textureSample(tDetail,sLinearRepeat,p.xz*0.09);let rockW=smoothstep(0.35,0.65,rockAmt+(tri.r-0.5)*0.5+(broad.r-0.5)*0.3);let camDist=length(p-frame.camPos);let rippleStrength=(1.0-rockW)*smoothstep(0.3,0.05,1.0-n.y)*smoothstep(0.25,0.7,broad.g+broad.r*0.4)*mix(1.0,0.15,smoothstep(15.0,40.0,camDist));let e=0.22;let r0=textureSample(tDetail,sLinearRepeat,p.xz*0.07).b;let rx=textureSample(tDetail,sLinearRepeat,(p.xz+vec2f(e,0.0))*0.07).b;let rz=textureSample(tDetail,sLinearRepeat,(p.xz+vec2f(0.0,e))*0.07).b;let f0s=textureSample(tDetail,sLinearRepeat,p.zx*0.31).b;let fxs=textureSample(tDetail,sLinearRepeat,(p.zx+vec2f(e*0.25,0.0))*0.31).b;let fzs=textureSample(tDetail,sLinearRepeat,(p.zx+vec2f(0.0,e*0.25))*0.31).b;let sandGrad=vec2f(rx-r0,rz-r0)*rippleStrength*0.8+vec2f(fzs-f0s,fxs-f0s)*(1.0-rockW)*0.5;let c0=tri.g;let sandN=normalize(vec3f(n.x-sandGrad.x,n.y,n.z-sandGrad.y));let reefAmt=smoothstep(0.3,0.7,m.g)*rockW;let grazing=max(abs(dot(n,normalize(frame.camPos-p))),0.15);let rockN=rockBump(n,p,reefAmt,0.26,clamp(camDist*0.004/sqrt(grazing),0.04,0.6));n=normalize(mix(sandN,rockN,rockW));let speckle=smoothstep(0.62,0.75,triFine.a);var sandCol=mix(vec3f(0.62,0.53,0.40),vec3f(0.86,0.78,0.62),smoothstep(0.25,0.75,broad.r));sandCol*=(0.9+0.2*sandDetail.a)*mix(0.94+0.1*r0,0.99,smoothstep(15.0,40.0,camDist))*(1.0-speckle*0.35);var rockCol=mix(vec3f(0.17,0.15,0.13),vec3f(0.36,0.32,0.27),tri.r)*(0.8+0.25*triFine.r);rockCol*=mix(0.78,1.0,smoothstep(0.25,0.75,broad.r*0.7+triFine.a*0.3));let hueSel=triplanar(p,n,0.045).r;let algae=vec3f(0.21,0.21,0.1);let coralline=vec3f(0.5,0.33,0.3);let sponge=vec3f(0.34,0.17,0.36);var growth=mix(algae,coralline,smoothstep(0.42,0.58,hueSel));growth=mix(growth,sponge,smoothstep(0.62,0.72,hueSel)*0.8);growth*=0.85+0.3*triFine.r;let growthAmt=clamp((mossAmt+0.25)*smoothstep(0.3,0.75,tri.r*0.75+triFine.r*0.25+broad.r*0.4)*smoothstep(0.2,0.6,n.y),0.0,0.9);rockCol=mix(rockCol,growth,growthAmt);let rubbleField=triplanar(p,n,0.42);let lump=smoothstep(0.35,0.7,rubbleField.r*0.7+tri.r*0.3);let fragment=smoothstep(0.66,0.76,triplanar(p,n,0.9).r)*smoothstep(0.4,0.9,n.y);let turfCol=mix(vec3f(0.2,0.19,0.1),vec3f(0.3,0.25,0.13),triFine.r);var reefCol=mix(rockCol*0.8,turfCol,lump*0.8);reefCol=mix(reefCol,vec3f(0.66,0.62,0.54)*(0.8+0.3*rubbleField.b),fragment*0.7);rockCol=mix(rockCol,reefCol,reefAmt);let micro=triplanar(p,n,2.3);rockCol*=0.78+0.44*micro.r;rockCol=mix(rockCol,vec3f(0.62,0.58,0.5),smoothstep(0.74,0.84,micro.a)*0.5);rockCol=mix(rockCol,rockCol*0.45,smoothstep(0.3,0.18,micro.r)*0.6);var s=defaultSurface();s.albedo=mix(sandCol,rockCol,rockW);s.roughness=mix(0.92,0.75,rockW);s.normal=n;s.ao=aoIn*mix(1.0,0.6+0.4*smoothstep(0.2,0.7,tri.r),rockW);s.f0=0.03;return s;}`;

const renderShader = /* wgsl */ `${surfaceLib} ${propsWgsl} struct Grid{count:u32,worldSize:f32};@group(1)@binding(0)var<uniform>grid:Grid;@group(1)@binding(1)var<storage,read>chunks:array<vec4f>;fn chunkVertex(vi:u32,ii:u32)->vec3f{let c=chunks[ii];let cells=u32(c.w);let n=cells+1u;var lx:u32;var lz:u32;var skirt=0.0;if(vi<n*n){lx=vi%n;lz=vi/n;}else{let k=vi-n*n;let m=cells;if(k<m){lx=k;lz=0u;}else if(k<2u*m){lx=m;lz=k-m;}else if(k<3u*m){lx=m-(k-2u*m);lz=m;}else{lx=0u;lz=m-(k-3u*m);}skirt=1.0;}let gx=(c.x+f32(lx)*c.z)/f32(grid.count);let gz=(c.y+f32(lz)*c.z)/f32(grid.count);return vec3f(gx,gz,skirt*c.z);}fn chunkWorld(vi:u32,ii:u32)->vec3f{let cv=chunkVertex(vi,ii);let g=cv.xy*2.0-1.0;let warped=sign(g)*pow(abs(g),vec2f(1.6));let xz=warped*grid.worldSize*0.5;let h=textureSampleLevel(tTerrain,sLinearClamp,xz/frame.terrain.x+0.5,0.0).r;return vec3f(xz.x,h-cv.z*0.35,xz.y);}struct VOut{@builtin(position)pos:vec4f,@location(0)world:vec3f,@location(1)uv:vec2f,@location(2)prevClip:vec4f,@location(3)curClip:vec4f,};fn terrainUv(xz:vec2f)->vec2f{return xz/frame.terrain.x+0.5;}@vertex fn vs(@builtin(vertex_index)vi:u32,@builtin(instance_index)ii:u32)->VOut{let world=chunkWorld(vi,ii);let uv=terrainUv(world.xz);var o:VOut;o.pos=frame.viewProj*vec4f(world,1.0);o.world=world;o.uv=uv;o.curClip=frame.viewProjNoJitter*vec4f(world,1.0);o.prevClip=frame.prevViewProjNoJitter*vec4f(world,1.0);return o;} ${terrainMaterialWgsl} @fragment fn fs(i:VOut)->FOut{let t=textureSample(tTerrain,sLinearClamp,i.uv);let m=textureSample(tTerrainMask,sLinearClamp,i.uv);var n=normalize(vec3f(t.g,sqrt(max(1.0-t.g*t.g-t.b*t.b,0.0)),t.b));let p=i.world;let s=terrainSurface(p,n,m,t.a);let lit=shadeSurface(s,p,-1.0);var o:FOut;o.color=vec4f(applyWater(lit,p),1.0);o.velocity=screenVelocity(i.curClip,i.prevClip);o.id=idOf(CAT_GROUND);return o;}@fragment fn fsFlat(i:VOut,@builtin(front_facing)front:bool)->FOut{let t=textureSample(tTerrain,sLinearClamp,i.uv);let n=normalize(vec3f(t.g,sqrt(max(1.0-t.g*t.g-t.b*t.b,0.0)),t.b));let key=clamp(dot(n,normalize(vec3f(0.4,0.85,0.3))),0.0,1.0);var o:FOut;o.color=vec4f(select(vec3f(1.2,0.0,0.0),vec3f(0.55,0.6,0.62)*(0.25+0.75*key),front),1.0,);o.velocity=screenVelocity(i.curClip,i.prevClip);o.id=idOf(CAT_GROUND);return o;}@vertex fn vsShadow(@builtin(vertex_index)vi:u32,@builtin(instance_index)ii:u32)->@builtin(position)vec4f{return frame.shadowViewProj*vec4f(chunkWorld(vi,ii),1.0);}`;

export interface TerrainRenderer {
  /** Picks visible chunks and their detail levels for this frame. */
  update(view: CullView): void;
  /** Chunks and triangles submitted this frame, for the on-screen readout. */
  readonly stats: {chunks: number; triangles: number};
  draw(pass: GPURenderPassEncoder): void;
  drawShadow(pass: GPURenderPassEncoder): void;
}

export async function createTerrainRenderer(
  device: GPUDevice,
  globalsLayout: GPUBindGroupLayout,
  targets: {
    color: GPUTextureFormat;
    velocity: GPUTextureFormat;
    id: GPUTextureFormat;
    depth: GPUTextureFormat;
  },
  gridCount: number,
  worldSize: number,
  heightAt: (x: number, z: number) => number,
  debug = false,
): Promise<TerrainRenderer> {
  const module = createShader(device, 'terrain:render-shader', renderShader);
  const localLayout = device.createBindGroupLayout({
    label: 'terrain:local-bgl',
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX,
        buffer: {type: 'uniform', minBindingSize: 8},
      },
      {
        binding: 1,
        visibility: GPUShaderStage.VERTEX,
        buffer: {type: 'read-only-storage'},
      },
    ],
  });
  const layout = device.createPipelineLayout({
    label: 'terrain:pipeline-layout',
    bindGroupLayouts: [globalsLayout, localLayout],
  });
  const pipeline = await device.createRenderPipelineAsync({
    label: 'terrain:pipeline',
    layout,
    vertex: {module, entryPoint: 'vs'},
    fragment: {
      module,
      entryPoint: debug ? 'fsFlat' : 'fs',
      targets: [
        {format: targets.color},
        {format: targets.velocity},
        {format: targets.id},
      ],
    },
    primitive: {topology: 'triangle-list', cullMode: 'back'},
    depthStencil: {
      format: targets.depth,
      depthWriteEnabled: true,
      depthCompare: 'greater',
    },
  });
  const shadowPipeline = await device.createRenderPipelineAsync({
    label: 'terrain:shadow-pipeline',
    layout,
    vertex: {module, entryPoint: 'vsShadow'},
    primitive: {topology: 'triangle-list', cullMode: 'none'},
    depthStencil: {
      format: 'depth32float',
      depthWriteEnabled: true,
      depthCompare: 'greater',
    },
  });

  const gridBuf = device.createBuffer({
    label: 'terrain:grid-uniform',
    size: 8,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const gridData = new ArrayBuffer(8);
  new Uint32Array(gridData, 0, 1)[0] = gridCount;
  new Float32Array(gridData, 4, 1)[0] = worldSize;
  device.queue.writeBuffer(gridBuf, 0, gridData);

  // --- Chunks ---
  // The grid is split into square chunks; each is drawn at one of several
  // detail levels (cell steps 1, 2, 4, 8) as an instance of that level's
  // shared index buffer.
  const CHUNK = Math.min(32, gridCount);
  const perSide = Math.ceil(gridCount / CHUNK);
  const steps = [1, 2, 4, 8].filter(st => st <= CHUNK);
  const warp = (g: number) => Math.sign(g) * Math.pow(Math.abs(g), 1.6);
  const chunkInfo: {
    ix: number;
    iz: number;
    cells: number;
    min: [number, number, number];
    max: [number, number, number];
  }[] = [];
  for (let cz = 0; cz < perSide; cz++) {
    for (let cx = 0; cx < perSide; cx++) {
      const ix = cx * CHUNK;
      const iz = cz * CHUNK;
      const cells = Math.min(CHUNK, gridCount - ix, gridCount - iz);
      const x0 = warp((ix / gridCount) * 2 - 1) * worldSize * 0.5;
      const x1 = warp(((ix + cells) / gridCount) * 2 - 1) * worldSize * 0.5;
      const z0 = warp((iz / gridCount) * 2 - 1) * worldSize * 0.5;
      const z1 = warp(((iz + cells) / gridCount) * 2 - 1) * worldSize * 0.5;
      // Every cell corner, not a coarse sample of them: canyon walls put
      // metres of height inside one chunk, and a bound that misses them
      // culls chunks that are still on screen.
      let hMin = Infinity;
      let hMax = -Infinity;
      for (let sz = 0; sz <= cells; sz++) {
        for (let sx = 0; sx <= cells; sx++) {
          const h = heightAt(
            x0 + ((x1 - x0) * sx) / cells,
            z0 + ((z1 - z0) * sz) / cells,
          );
          hMin = Math.min(hMin, h);
          hMax = Math.max(hMax, h);
        }
      }
      chunkInfo.push({
        ix,
        iz,
        cells,
        // Margin for the skirts that hide the seams between levels.
        min: [Math.min(x0, x1), hMin - 3.5, Math.min(z0, z1)],
        max: [Math.max(x0, x1), hMax + 0.5, Math.max(z0, z1)],
      });
    }
  }

  // One index buffer per level (a full chunk of CHUNK cells), with skirts.
  const levelIndex: {buf: GPUBuffer; count: number; cells: number}[] =
    steps.map(step => {
      const cells = CHUNK / step;
      const n = cells + 1;
      const idx: number[] = [];
      for (let z = 0; z < cells; z++) {
        for (let x = 0; x < cells; x++) {
          const i0 = z * n + x;
          const i1 = i0 + 1;
          const i2 = i0 + n;
          const i3 = i2 + 1;
          // Counter-clockwise when seen from above (+y).
          idx.push(i0, i2, i1, i1, i2, i3);
        }
      }
      const m = cells;
      const ringTop = (k: number) => {
        k %= 4 * m;
        if (k < m) return k;
        if (k < 2 * m) return (k - m) * n + m;
        if (k < 3 * m) return m * n + (m - (k - 2 * m));
        return (m - (k - 3 * m)) * n;
      };
      for (let k = 0; k < 4 * m; k++) {
        const a = ringTop(k);
        const b = ringTop(k + 1);
        const aDown = n * n + k;
        const bDown = n * n + ((k + 1) % (4 * m));
        // Both windings: skirts are seen from either side.
        idx.push(a, aDown, b, b, aDown, bDown, a, b, aDown, b, bDown, aDown);
      }
      const data = new Uint32Array(idx);
      const buf = device.createBuffer({
        label: `terrain:index-buffer-step${step}`,
        size: data.byteLength,
        usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      });
      device.queue.writeBuffer(buf, 0, data);
      return {buf, count: data.length, cells};
    });

  const maxChunks = chunkInfo.length * 2;
  const chunkData = new Float32Array(maxChunks * 4);
  const chunkBuf = device.createBuffer({
    label: 'terrain:chunks',
    size: chunkData.byteLength,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const localBindGroup = device.createBindGroup({
    label: 'terrain:local-bind-group',
    layout: localLayout,
    entries: [
      {binding: 0, resource: {buffer: gridBuf}},
      {binding: 1, resource: {buffer: chunkBuf}},
    ],
  });

  // Per pass and level: a contiguous run of instances in the chunk buffer.
  // Edge chunks with fewer cells are drawn at a level whose cell count fits.
  type Run = {first: number; count: number};
  const camRuns: Run[] = steps.map(() => ({first: 0, count: 0}));
  const shadowRuns: Run[] = steps.map(() => ({first: 0, count: 0}));
  const lists: number[][] = steps.map(() => []);
  const shadowLists: number[][] = steps.map(() => []);

  const camPlanes = new Float32Array(16);
  const shadowPlanes = new Float32Array(16);
  const stats = {chunks: 0, triangles: 0};

  return {
    stats,
    update(view: CullView) {
      const cp = view.camPos;
      sidePlanes(view.viewProj, camPlanes);
      sidePlanes(view.shadowViewProj, shadowPlanes);
      lists.forEach(l => (l.length = 0));
      shadowLists.forEach(l => (l.length = 0));
      chunkInfo.forEach((c, i) => {
        // Distance from the camera to the chunk's box.
        const dx = Math.max(c.min[0] - cp[0], 0, cp[0] - c.max[0]);
        const dy = Math.max(c.min[1] - cp[1], 0, cp[1] - c.max[1]);
        const dz = Math.max(c.min[2] - cp[2], 0, cp[2] - c.max[2]);
        const dist = Math.hypot(dx, dy, dz);
        // Coarser while one cell stays under ~1/40 of the distance (a few pixels).
        const cellWorld = (c.max[0] - c.min[0]) / c.cells;
        const pick = (tolerance: number) => {
          let level = 0;
          while (
            level < steps.length - 1 &&
            c.cells % steps[level + 1] === 0 &&
            cellWorld * steps[level + 1] < Math.max(dist, 0.5) * tolerance
          ) {
            level++;
          }
          return level;
        };
        if (!boxOutside(camPlanes, c.min, c.max)) {
          lists[pick(0.025)].push(i);
        }
        if (!boxOutside(shadowPlanes, c.min, c.max)) {
          shadowLists[pick(0.08)].push(i);
        }
      });
      let cursor = 0;
      const pack = (src: number[][], runs: Run[]) => {
        src.forEach((list, level) => {
          runs[level].first = cursor;
          runs[level].count = 0;
          for (const i of list) {
            const c = chunkInfo[i];
            const cellsAtLevel = c.cells / steps[level];
            // Partial edge chunks: only draw if they tile the level's grid.
            if (cellsAtLevel !== levelIndex[level].cells) {
              continue;
            }
            chunkData.set([c.ix, c.iz, steps[level], cellsAtLevel], cursor * 4);
            cursor++;
            runs[level].count++;
          }
        });
      };
      pack(lists, camRuns);
      pack(shadowLists, shadowRuns);
      stats.chunks = camRuns.reduce((n, r) => n + r.count, 0);
      stats.triangles = camRuns.reduce(
        (n, r, level) => n + (r.count * levelIndex[level].count) / 3,
        0,
      );
      if (cursor) {
        device.queue.writeBuffer(chunkBuf, 0, chunkData, 0, cursor * 4);
      }
    },
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, localBindGroup);
      camRuns.forEach((r, level) => {
        if (r.count) {
          pass.setIndexBuffer(levelIndex[level].buf, 'uint32');
          pass.drawIndexed(levelIndex[level].count, r.count, 0, 0, r.first);
        }
      });
    },
    drawShadow(pass) {
      pass.setPipeline(shadowPipeline);
      pass.setBindGroup(1, localBindGroup);
      shadowRuns.forEach((r, level) => {
        if (r.count) {
          pass.setIndexBuffer(levelIndex[level].buf, 'uint32');
          pass.drawIndexed(levelIndex[level].count, r.count, 0, 0, r.first);
        }
      });
    },
  };
}
